import {Component, OnInit, Inject, HostListener, ElementRef} from '@angular/core';
import * as angular from 'angular';
import {downgradeComponent} from '@angular/upgrade/static';
import {CookieService} from 'ngx-cookie';
import * as _ from 'lodash';
import {ApiService} from '../../api/api.service';
import {SettingsService} from '../settings.service';
import {EMPLOYEE_PROPERTIES_BITMASK_SERVICE, EmployeePropertiesBitmaskService} from '../../shared/upgrade/employee-properties-bitmask.service';
import {Employee, EmployeeEditHistoryEntry} from 'resteasytox';
import {UserService} from '../../shared/user.service';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Observable} from 'rxjs/observable';
import {
	EmployeeEditHistoryDetails,
	EmployeesEditHistoryCacheService
} from '../../core/employee-edit-history-cache.service';
import {Duration} from 'moment';
import * as moment from 'moment';
import {VIEW_DATE_FORMAT} from '../../reports/timecards-builder.service';

interface ErrorFields {
	firstName?: string;
	lastName?: string;
	email?: string;
	loginName?: string;
	// payType?: string;
	qbName?: string;
	startWork?: string;
	color?: string;
	password?: string;
}

interface FilterRole {
	name: string;
	paramName: string;
	value: boolean;
	count: number;
}

export enum EmployeeType {
	DAILY_1099,
	HOURLY_1099,
	DAILY_W2,
	HOURLY_W2
}

interface EmployeeCurrentDetails extends EmployeeEditHistoryDetails {
	startWork: string;
	finishWork?: string;
}


@Component({
	selector: 'tt-settings-employees',
	templateUrl: './settings-employees.component.html',
	styleUrls: ['../settings.component.scss', './settings-employees.component.scss']
})
export class SettingsEmployeesComponent implements OnInit {
	public addMode: boolean = false;
	public editMode: boolean = false;
	public enableSaveWarning: boolean = false;
	public displayedEmployees: Employee[];
	public formErrors: any = {};
	public selectedEmployee: Employee;
	public selectedEmployeeCurrentDetails: EmployeeCurrentDetails;
	public editEmployee: Employee;
	public newEmployee: Employee = new Employee();
	public displayColorsList: boolean = false;


	public readonly colorsList: any[] = [
		{
			body: 'f07d88',
			border: 'ed616f'
		},
		{
			body: 'ff8c00',
			border: 'fb6f25'
		},
		{
			body: 'fecb6f',
			border: 'e1b46d'
		},
		{
			body: 'fff100',
			border: 'fcd146'
		},
		{
			body: '5fbe7d',
			border: '56a659'
		},
		{
			body: '33bab1',
			border: '3fa296'
		},
		{
			body: 'a3b367',
			border: '82a034'
		},
		{
			body: '55abe5',
			border: '3096d0',
		},
		{
			body: 'a895e2',
			border: '8c82d0'
		},
		{
			body: 'e48bb5',
			border: 'd475a7'
		},
		{
			body: 'b9c0cb',
			border: '9ba9bc'
		},
		{
			body: '4c596e',
			border: '343e4f'
		},
		{
			body: 'ababab',
			border: '969696',
		},
		{
			body: '666666',
			border: '4f4f4f'
		},
		{
			body: 	'474747',
			border: '212121'
		},
		{
			body: 	'910a19',
			border: '62030d'
		},
		{
			body: 	'ce4b28',
			border: 'bc2e1b',
		},
		{
			body: 	'a47332',
			border: '9d5727'
		},
		{
			body: 	'b0a923',
			border: '9b9137'
		},
		{
			body: 	'026802',
			border: '0e440e'
		},
		{
			body: 	'1c6367',
			border: '143f43'
		},
		{
			body: 	'5c6a22',
			border: '384f11'
		},
		{
			body: 	'254069',
			border: '111738'
		},
		{
			body: 	'562685',
			border: '1e0d3b'
		},
		{
			body: 	'80275d',
			border: '5b0743'
		}
	];
	public readonly stateCookieName: string = 'employeesPageState';
	public readonly filterRoles: FilterRole[] = [ // don't change order of the items.
		{
			name: 'Admins',
			paramName: 'admin',
			value: false,
			count: 0
		},
		{
			name: 'Inactive Users',
			paramName: 'includeDisabled',
			value: false,
			count: 0
		},
		{
			name: 'TimeTracker Admin',
			paramName: 'ttAdmin',
			value: false,
			count: 0
		},
		{
			name: 'QuickBooks Admin',
			paramName: 'qbAdmin',
			value: false,
			count: 0
		}
	];
	public readonly EMPLOYEE_TYPE: any = EmployeeType;
	constructor(private settingsService: SettingsService,
				private cookieService: CookieService,
				@Inject(EMPLOYEE_PROPERTIES_BITMASK_SERVICE) private employeePropertiesBitmaskService: EmployeePropertiesBitmaskService,
				private apiService: ApiService,
				private userService: UserService,
				private employeesEditHistoryCacheService: EmployeesEditHistoryCacheService
	) {
	}

	ngOnInit(): void {
		this.initNewEmployee();
		this.adjustData().subscribe((res: any) => {
			const state: any = this.getCookieState();
			const employees: Employee[] = res[0];
			if (state) {
				const employeeId: number = state['employeeId'];
				let employee: Employee;
				for (let i: number = 0; i < employees.length; i++) {
					employee = employees[i];
					if (employee.id === employeeId) {
						this.selectEmployee(employee);
						break;
					}
				}
			} else {
				this.selectEmployee(employees[0]);
			}
		});
	}

	@HostListener('window:beforeunload', ['$event'])
	unloadNotification(evt: any): void {
		if (this.enableSaveWarning) {
			if (!confirm('You have unsaved changes. Are you sure you want to leave this page?')) {
				evt.preventDefault();
			}
		}
	}

	public adjustData(callback?: Function): Observable<any> {
		const filterParams: {[key: string]: boolean} = this.getRoleParams();
		return forkJoin(
			this.apiService.getAllEmployees({
				params: filterParams
			}),
			this.apiService.getAllEmployeesRolesCount()
		)
			.map((res: any[]) => {
				let employees: Employee[] = res[0];
				const count: {[key: string]: number} = res[1];

				if (filterParams.includeDisabled) {
					employees = _.filter(employees, ['disabled', true]);
				}
				this.displayedEmployees = _.orderBy(employees, ['firstName']);
				this.updateRolesCount(count[0]);
				if (_.isFunction(callback)) {
					callback(employees);
				}

				return res;
			});
	}

	public selectEmployee(employee: Employee): void {
		if (this.addMode) {
			if (confirm('Add form will be closed. Do you want continue?')) {
				this.disableAddMode();
				this.selectedEmployee = employee;
				this.selectedEmployeeCurrentDetails = this.getEmployeeCurrentDetails(this.selectedEmployee.id);
				return;
			}
		}

		if (this.editMode) {
			if (confirm('Add form will be closed. Do you want continue?')) {
				this.disableEditMode();
				this.selectedEmployee = employee;
				this.selectedEmployeeCurrentDetails = this.getEmployeeCurrentDetails(this.selectedEmployee.id);
				return;
			}
		}

		this.selectedEmployee = employee;
		this.selectedEmployeeCurrentDetails = this.getEmployeeCurrentDetails(this.selectedEmployee.id);
	}

	public getEmployeeCurrentDetails(employeeId: number): EmployeeCurrentDetails {
		const details: EmployeeEditHistoryDetails = this.employeesEditHistoryCacheService.getEmployeeDetailsAtDate(employeeId, moment().startOf('day').valueOf());
		const employeeEditHistory: EmployeeEditHistoryEntry[] = this.employeesEditHistoryCacheService.getEmployeeEditHistory(employeeId);
		if (!details || !employeeEditHistory || employeeEditHistory.length === 0) {
			alert('Warning! Person details is not loaded. Most likely Person Pay Status History does not contain entry for today');
			return null;
		}
		const startDate: number = _.first(employeeEditHistory).startDate;
		const endDate: number = _.last(employeeEditHistory).endDate;

		return _.merge(details, {
			startWork: moment(startDate).format(VIEW_DATE_FORMAT),
			finishWork: (endDate) ? moment(endDate).format(VIEW_DATE_FORMAT) : ''
		});
	}

	public saveEmployee(employee: Employee): void {
		const user: Employee = this.editEmployee;
		const errors: any = this.validateEmployee(user);
		this.formErrors = errors;
		if (!_.isEmpty(errors)) {
			return;
		}
		if (user['finishWork'] && user['finishWork'].toString() === '') {
			delete user['finishWork'];
		}
		if (user['color'] !== undefined) {
			user['color'] = user['color'].replace('#', '');
		}

		if (user['startWorkTime']) {
			user['startWorkTimeDuration'] = this.convert12TimeTo24(user['startWorkTime']);
		}

		if (user['finishWorkTime']) {
			user['finishWorkTimeDuration'] = this.convert12TimeTo24(user['finishWorkTime']);
		}

		this.settingsService.createEmployee(user).subscribe(
			() => {
				this.disableEditMode();
				this.selectEmployee(user);
				this.adjustData().subscribe(() => {});
				this.enableSaveWarning = false;
			}
		);
	}

	public isValidInput(field: string): boolean {
		return this.formErrors[field] === undefined;
	}

	public getErrorMessage(field: string): string {
		return this.formErrors[field];
	}

	public enableEditMode(): void {
		this.editEmployee = Object.assign({}, this.selectedEmployee);
		this.enableSaveWarning = true;
		this.formErrors = {};
		this.editMode = true;
	}

	public disableEditMode(newEmployee?: Employee): void {
		this.enableSaveWarning = false;
// TODO: refactor to remove 'editMode' property from employee object
		this.editMode = false;
	}

	public enableAddMode(): void {
		// this.selectedEmployee = {};
		this.formErrors = {};
		this.newEmployee.admin = false;
		this.newEmployee.qbAdmin = false;
		this.newEmployee.disabled = false;
		this.newEmployee.ttAdmin = false;
		this.newEmployee.timeAdmin = false;
		this.addMode = true;
	}

	public disableAddMode(): void {
		this.initNewEmployee();
		this.addMode = false;
	}

	public saveNewEmployee(): void {
		const errors: any = this.validateEmployee(this.newEmployee, true);
		this.formErrors = errors;
		if (!_.isEmpty(errors)) {
			return;
		}

		if (this.newEmployee['startWorkTime']) {
			this.newEmployee['startWorkTimeDuration'] = this.convert12TimeTo24(this.newEmployee['startWorkTime']);
		}

		if (this.newEmployee['finishWorkTime']) {
			this.newEmployee['finishWorkTimeDuration'] = this.convert12TimeTo24(this.newEmployee['finishWorkTime']);
		}


		this.settingsService.createEmployee(this.newEmployee)
			.mergeMap(() => {
				return this.employeesEditHistoryCacheService.updateData();
			})
			.subscribe(() => {
					alert('New user added');
					this.addMode = false;
					const self: SettingsEmployeesComponent = this;
					this.adjustData().subscribe(() => {
						self.selectEmployee(_.find(self.displayedEmployees, {loginName: self.newEmployee.loginName}));
						self.initNewEmployee();
					});
					this.enableSaveWarning = false;
				}
			);
	};

	public toggleSettingsBitmask(event: any, setting: string, employee: Employee): void {
		const isSelected: boolean = event.target.checked;
		let bitmask: number = employee.settingsBitmask;
		if (isSelected) {
			bitmask = this.employeePropertiesBitmaskService.enableProperty(setting, bitmask);
		} else {
			bitmask = this.employeePropertiesBitmaskService.disableProperty(setting, bitmask);
		}
		employee.settingsBitmask = bitmask;
	}

	public isSelectedSetting(setting: string, employee: Employee): boolean {
		return this.employeePropertiesBitmaskService.hasProperty(setting, employee.settingsBitmask);
	}

	public toggleRoleFilter(): void {
		this.adjustData().subscribe(() => {});
	}

	public getRoleParams(): {[key: string]: boolean} {
		const result: any = {};
		this.filterRoles.forEach( (role: FilterRole) => {
			result[role.paramName] = role.value;
		});
		return result;
	}

	public selectEmployeeColor(employee: Employee, color: string): void {
		employee.color = color;
		this.displayColorsList = false;
		this.hideColorsList();
	}

	public showColorsList(): void {
		this.displayColorsList = true;
	}

	public hideColorsList(): void {
		setTimeout(() => {
			this.displayColorsList = false;
		}, 100);
	}

	// public formatPayTypeToReadable(value: 'daily' | 'hourly'): string {
	// 	if (value === 'hourly') {
	// 		return 'hourly';
	// 	} else if (value === 'daily') {
	// 		return 'daily';
	// 	}
	// }

	public formatEmployeeTypeToReadable(value: EmployeeType): string {
		value = parseInt(value.toString(), 10);
		if (value === EmployeeType.DAILY_1099) {
			return '1099 Daily';
		} else if (value === EmployeeType.HOURLY_1099) {
			return '1099 Hourly';
		} else if (value === EmployeeType.DAILY_W2) {
			return 'W-2 Daily';
		} else if (value === EmployeeType.HOURLY_W2) {
			return 'W-2 Hourly';
		}
	}

	public loginAsEmployee(e: Event, employee: Employee): void {
		e.preventDefault();
		e.stopPropagation();

		const baseUrl: string = document.location.origin;

		const $userAk: Observable<any> = this.settingsService.getEmployeeAccessKey(employee.id);
		const $adminAk: Observable<any> = this.settingsService.getEmployeeAccessKey(this.userService.user.id);

		forkJoin([$userAk, $adminAk])
			.subscribe((res: any) => {
				if (res[0].success && res[0].payload) {
					if (!res[1].payload) {
						alert('Set access key for your account first');
						return;
					}
					this.cookieService.putObject('admin_mode', { name: `${this.userService.user.firstName} ${this.userService.user.lastName}`, accessKey: res[1].payload  });
					const accessKey: any = res[0].payload;
					document.location.href = `${baseUrl}/login.html?access_key=${accessKey}`;
				}
			});
	}

	public canLoginAsEmployee(): boolean {
		return this.userService.user && this.userService.user.admin;

	}

	public formatWorkTime(milliseconds: number): string {
		const dur: Duration = moment.duration(milliseconds);
		let isPm: boolean = false;
		let hours: number = dur.hours();
		if (hours >= 12) { hours -= 12; isPm = true; }

		if (isPm) {
			return `${hours}:${dur.minutes()}pm`;
		} else {
			return `${hours}:${dur.minutes()}am`;
		}
	}

// Private methods

	private convert12TimeTo24(time: string): number {
		const blankRegex: RegExp =  /\s+/gmi;
		time = time.replace(blankRegex, '').toLowerCase();

		const postfix: string = (time.search('pm') !== -1) ? 'pm' : 'am';
		time = time.replace('am', '').replace('pm', '');
		if (postfix === 'am') {
			return moment.duration(time).asMilliseconds();
		} else {
			return moment.duration(time).add(12, 'hours').asMilliseconds();
		}
	}

	private updateRolesCount(count: number): void {
		this.filterRoles.forEach((role: FilterRole) => {
			role.count = count[role.paramName];
		});
	}

	private validateEmployee(employee: Employee, isNew?: boolean): ErrorFields {
		const errors: ErrorFields = {};
		const requiredFields: string[] = ['firstName', 'lastName', 'email', 'loginName', 'employeeType', 'qbName', 'startWork', 'color'];
		if (isNew) {
			requiredFields.push('password');
		}
		requiredFields.forEach((field: string) => {
			if (employee[field] === null || employee[field] === '') {
				errors[field] = field + ' is required';
			}
		});

		return errors;
	}

	private initNewEmployee(): void {
		this.newEmployee = new Employee().initFromData({
			settingsBitmask: 0 << 32,
			startWorkTime: '8:30 am',
			finishWorkTime: '5:30 pm'
		});
	}

	private getCookieState(): any {
		return this.cookieService.getObject(this.stateCookieName);
	}
}

angular.module('timetracker2')
	.directive(
		'ttSettingsEmployees',
		downgradeComponent({ component: SettingsEmployeesComponent }) as angular.IDirectiveFactory
	);



// WEBPACK FOOTER //
// ./src/app/settings/settings-employees/settings-employees.component.ts